import { useState } from "react";
import { CATEGORY_LABELS } from "../../lib/constants";
import { useLocation } from "../../contexts/LocationContext";

export default function PostForm({ initial = {}, onSubmit, submitLabel = "Publish", disableTypeCategory = false }) {
  const { latitude, longitude } = useLocation();
  const [form, setForm] = useState({
    type: initial.type || "request",
    category: initial.category || "volunteering",
    title: initial.title || "",
    description: initial.description || "",
    latitude: initial.latitude ?? latitude ?? "",
    longitude: initial.longitude ?? longitude ?? "",
    takers_needed: initial.takers_needed ?? "",
    needed_by_start: initial.needed_by_start || "",
    needed_by_end: initial.needed_by_end || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const data = {
        ...form,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
      };
      if (form.type === "request" && form.takers_needed) {
        data.takers_needed = Number(form.takers_needed);
      } else {
        data.takers_needed = null;
      }
      if (!form.needed_by_end) data.needed_by_end = null;
      await onSubmit(data);
    } catch (err) {
      setError(err.error || err.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-xl mx-auto">
      <div>
        <label className="block text-sm font-medium text-walnut mb-2">What are you posting?</label>
        <div className="flex gap-3">
          {["request", "offer"].map((t) => (
            <button
              key={t}
              type="button"
              disabled={disableTypeCategory}
              onClick={() => update("type", t)}
              className={`flex-1 py-3 rounded-md text-sm font-medium border transition-colors ${
                form.type === t
                  ? "bg-terracotta text-white border-terracotta"
                  : "bg-cream text-walnut-light border-stone hover:border-terracotta/30"
              } ${disableTypeCategory ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              {t === "request" ? "Request" : "Offer"}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-walnut mb-2">Category</label>
        <select
          value={form.category}
          onChange={(e) => update("category", e.target.value)}
          disabled={disableTypeCategory}
          className="w-full px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut focus:outline-none focus:ring-2 focus:ring-terracotta/30 disabled:opacity-60"
        >
          {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-walnut mb-2">Title</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => update("title", e.target.value)}
          required
          className="w-full px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut placeholder:text-walnut-light/60 focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          placeholder="What do you need or what are you offering?"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-walnut mb-2">Description</label>
        <textarea
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          required
          rows={4}
          className="w-full px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut placeholder:text-walnut-light/60 focus:outline-none focus:ring-2 focus:ring-terracotta/30 resize-y"
          placeholder="Provide details about your post..."
        />
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-sm font-medium text-walnut mb-2">Needed by (start)</label>
          <input
            type="date"
            value={form.needed_by_start}
            onChange={(e) => update("needed_by_start", e.target.value)}
            required
            className="w-full px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-walnut mb-2">End (optional)</label>
          <input
            type="date"
            value={form.needed_by_end}
            onChange={(e) => update("needed_by_end", e.target.value)}
            className="w-full px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          />
        </div>
      </div>
      {form.type === "request" && (
        <div>
          <label className="block text-sm font-medium text-walnut mb-2">Helpers needed</label>
          <input
            type="number"
            min="1"
            value={form.takers_needed}
            onChange={(e) => update("takers_needed", e.target.value)}
            required
            className="w-32 px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-walnut mb-2">Location</label>
        <div className="flex gap-3">
          <input
            type="number"
            step="any"
            value={form.latitude}
            onChange={(e) => update("latitude", e.target.value)}
            required
            placeholder="Latitude"
            className="flex-1 px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          />
          <input
            type="number"
            step="any"
            value={form.longitude}
            onChange={(e) => update("longitude", e.target.value)}
            required
            placeholder="Longitude"
            className="flex-1 px-3 py-2.5 bg-cream border border-stone rounded-md text-sm text-walnut focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          />
        </div>
        <p className="text-xs text-walnut-light mt-1">Auto-filled from your browser location. Edit if needed.</p>
      </div>
      {error && <p className="text-sm text-coral">{error}</p>}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 bg-terracotta text-white rounded-md font-medium hover:bg-terracotta-dark transition-colors disabled:opacity-50"
      >
        {submitting ? "Saving..." : submitLabel}
      </button>
    </form>
  );
}
